import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { api, attempt } from '../../../lib/api';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export default function ManageRestrictedDomains() {
  const [restrictions, setRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ domain_name: '', reason: '' });

  useEffect(() => {
    fetchRestrictions();
  }, []);

  const fetchRestrictions = async () => {
    setLoading(true);
    const { data } = await attempt(api.get('admin/restricted-domains'));
    setRestrictions(data?.restrictions || []);
    setLoading(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const cleanDomain = formData.domain_name.toLowerCase().trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    
    const { data, error } = await attempt(api.post('admin/restricted-domains', { ...formData, domain_name: cleanDomain }));

    if (error) {
      alert(error.message);
    } else {
      // Add the returned row directly; an immediate re-fetch can miss it (NCB read lag).
      setRestrictions(prev => [...prev, data.restriction].sort((a, b) => a.domain_name.localeCompare(b.domain_name)));
      setFormData({ domain_name: '', reason: '' });
      setShowAdd(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Remove this domain from the restricted list?')) return;
    const { error } = await attempt(api.del(`admin/restricted-domains/${id}`));
    if (!error) setRestrictions(prev => prev.filter(r => r.id !== id));
  };

  const filtered = restrictions.filter(r => 
    r.domain_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restricted Domains</h1>
          <p className="text-gray-500 mt-1">Hostnames forbidden for custom domain use. Subdomains are automatically blocked.</p>
        </div>
        <div className="flex w-full md:w-auto gap-3">
          <div className="relative flex-1 md:w-64">
            <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search domains..." 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium" 
            />
          </div>
          <Button onClick={() => setShowAdd(!showAdd)} variant={showAdd ? 'secondary' : 'primary'}>
            <SafeIcon name={showAdd ? "X" : "Plus"} className="mr-2" />
            {showAdd ? 'Cancel' : 'Add Domain'}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.form 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleAdd}
            className="mb-8 p-6 bg-red-50 rounded-2xl border border-red-100 grid grid-cols-1 md:grid-cols-3 gap-4 items-end overflow-hidden"
          >
            <div>
              <label className="block text-xs font-bold text-red-700 mb-1 uppercase tracking-wider">Domain Name</label>
              <input 
                required 
                placeholder="example.com" 
                value={formData.domain_name} 
                onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
                className="w-full px-4 py-2 border border-red-200 rounded-xl font-bold outline-none" 
              />
              <p className="text-[10px] text-red-400 mt-1 italic">Blocking "example.com" also blocks "sub.example.com"</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-red-700 mb-1 uppercase tracking-wider">Reason</label>
              <input 
                value={formData.reason} 
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Why is this restricted?" 
                className="w-full px-4 py-2 border border-red-200 rounded-xl outline-none" 
              />
            </div>
            <Button type="submit" variant="danger" className="w-full">
              Add Restriction
            </Button>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100">
              <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Domain</th>
              <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Reason</th>
              <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-400 italic">Loading restrictions...</td></tr>
            ) : filtered.length > 0 ? (
              filtered.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">{item.domain_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{item.reason}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-gray-300 hover:text-red-600 transition-colors">
                      <SafeIcon name="Trash2" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-500 italic">No restricted domains found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
